import { useState, type FormEvent } from 'react';
import type { Draft, FieldsProps, TextKey } from './DestinationFields';
import { draftOf, inputOf, isStorageKind } from './destination-kinds';
import type {
  PaigeDestination,
  PaigeDestinationKind,
  PaigeDestinationsBridge,
  PaigeDestinationTest,
  PaigeContentCentralType,
  PaigeFieldMapping,
} from './paige.global';
import { trackProduct } from './product-tracker';
import { openConsentWindow } from './storage-connect';
import { connectIn, useStorageControls } from './use-storage-controls';

export interface DestinationFormProps {
  kind: PaigeDestinationKind;
  existing?: PaigeDestination;
  bridge: PaigeDestinationsBridge;
  /** After a save; a save hands the destination as Paige stored it. */
  onSaved: (saved?: PaigeDestination) => void;
}

/** One action at a time: busy while it runs, and its failure (Paige's reason) until the next one. */
function useBusyAction() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async (action: () => Promise<void>, failure: string) => {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : failure);
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, setError, run };
}

/**
 * The draft, the field props, and the save and check actions with their busy, note and
 * error state. A new storage destination is added and connected in one click: the consent window is
 * opened inside the click, the destination saved, and the window sent to the provider.
 */
export function useDestinationForm({ kind, existing, bridge, onSaved }: DestinationFormProps) {
  const [draft, setDraft] = useState<Draft>(() => draftOf(existing));
  const [note, setNote] = useState<string | null>(null);
  const [choices, setChoices] = useState<Record<string, string[]>>({});
  const { busy, error, setError, run } = useBusyAction();
  const storage = useStorageControls(bridge, existing, setError);
  const fields: FieldsProps = {
    draft,
    ...draftSetters(setDraft),
    secretSet: Boolean(existing?.secretSet),
    ...(existing ? { destinationId: existing.id } : {}),
    choices,
  };
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const consent = isStorageKind(kind) && !existing ? openConsentWindow() : undefined;
    return run(async () => {
      const saved = await bridge
        .save(inputOf(kind, draft), existing?.id)
        .catch((failure: unknown) => {
          consent?.close();
          throw failure;
        });
      if (consent !== undefined) await connectIn(bridge, saved.id, consent);
      trackProduct('export.configured', { destination: kind });
      onSaved(saved);
    }, 'Paige could not save the destination');
  };
  // A storage destination's test reads the stored folder, so what the sheet shows is saved first:
  // testing a folder chosen on screen but not saved said "choose the folder" (prod, 2026-10-01).
  const check = () =>
    existing
      ? run(async () => {
          if (isStorageKind(kind)) await bridge.save(inputOf(kind, draft), existing.id);
          await checkConnection(bridge, existing.id, setNote, setChoices);
        }, 'Paige could not reach the destination')
      : Promise.resolve();
  return { fields, busy: busy || storage.busy, error, note, submit, check, storage };
}

/** The fields' way of changing the draft: one text key at a time, the whole field map, or the type map. */
function draftSetters(setDraft: (update: (d: Draft) => Draft) => void) {
  const set = (key: TextKey) => (event: { target: { value: string } }) =>
    setDraft((d) => ({ ...d, [key]: event.target.value }));
  const setMap = (fieldMap: PaigeFieldMapping[]) => setDraft((d) => ({ ...d, fieldMap }));
  const setTypes = (types: PaigeContentCentralType[]) => setDraft((d) => ({ ...d, types }));
  return { set, setMap, setTypes };
}

/** Runs the test on a stored destination: its line goes in the note, its choices to the fields' suggestions. */
async function checkConnection(
  bridge: PaigeDestinationsBridge,
  id: string,
  setNote: (note: string) => void,
  setChoices: (choices: Record<string, string[]>) => void,
): Promise<void> {
  const result: PaigeDestinationTest = await bridge.test(id);
  setNote(result.detail);
  setChoices(Object.fromEntries((result.choices ?? []).map((c) => [c.setting, c.values])));
}
